/** Org-wide metadata search panel — type chips, grouped results, highlighting,
 *  and empty-state suggestions. */
import React, { Fragment } from "react";
import { Search, X } from "lucide-react";

import { PanelSection } from "./PanelSection";
import {
  formatRelativeTime,
  groupResultsByType,
  highlightTerm,
  SEARCH_FILTER_CHIPS,
} from "./searchHelpers";
import { useSearchState } from "./useSearchState";

interface SearchPanelProps {
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}

/** Inline renderer for a string with `term` matches wrapped in <mark>. */
const renderHighlighted = (text: string, term: string) =>
  highlightTerm(text, term).map((c, i) =>
    c.match ? (
      <mark key={i} className="bg-yellow-300/40 dark:bg-yellow-400/30 text-foreground rounded-sm px-0.5 font-bold">
        {c.text}
      </mark>
    ) : (
      <Fragment key={i}>{c.text}</Fragment>
    ),
  );

export function SearchPanel({ onOpenFileInEditor }: SearchPanelProps) {
  const {
    query, setQuery, results, facets, totalResults, loading,
    activeChip, setActiveChip,
  } = useSearchState();

  const groups = React.useMemo(() => groupResultsByType(results), [results]);
  const hasQuery = query.trim().length > 0;
  const showNoResults = hasQuery && !loading && results.length === 0;

  return (
    <PanelSection
      title="Search"
      icon={<Search className="w-3.5 h-3.5" />}
      count={hasQuery ? totalResults : 0}
    >
      <div className="space-y-3">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search org metadata..."
            className="w-full pl-9 pr-8 py-2 text-sm bg-card border-2 border-border rounded-md text-foreground shadow-sm placeholder:text-muted-foreground outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 transition-shadow"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Type filter chips */}
        <div className="flex flex-wrap gap-1.5">
          {SEARCH_FILTER_CHIPS.map((chip) => {
            const isActive = chip.id === activeChip.id;
            const facetCount = chip.types
              ? chip.types.reduce((sum, t) => sum + (facets[t] || 0), 0)
              : Object.values(facets).reduce((sum, n) => sum + n, 0);
            const showCount = hasQuery && facetCount > 0;
            return (
              <button
                key={chip.id}
                onClick={() => setActiveChip(chip)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full border-2 transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-muted/50 text-foreground border-border hover:bg-muted hover:border-foreground/25"
                }`}
                title={chip.types ? chip.types.join(", ") : "All types"}
              >
                {chip.label}
                {showCount && (
                  <span className={`ml-1.5 text-[10px] ${isActive ? "opacity-90" : "text-muted-foreground font-medium"}`}>
                    {facetCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="text-xs text-muted-foreground py-3 text-center font-medium">Searching…</div>
        )}

        {showNoResults && (
          <div className="text-xs text-foreground/80 py-3 text-center">
            No matches for &ldquo;{query}&rdquo;
          </div>
        )}

        {/* Grouped results — section per type */}
        {results.length > 0 && (
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {groups.map(({ type, items }) => (
              <div key={type} className="space-y-1">
                <div className="flex items-center gap-2 px-1 pb-0.5 border-b border-border/40">
                  <span className="text-[10px] uppercase tracking-wider text-foreground font-semibold">{type}</span>
                  <span className="text-[10px] text-muted-foreground font-bold">{items.length}</span>
                </div>
                {items.map((r) => {
                  const modified = formatRelativeTime(r.last_modified);
                  return (
                    <button
                      key={r.id}
                      onClick={() => onOpenFileInEditor({ id: r.id, name: r.name, metadataType: r.type })}
                      className="w-full text-left py-1.5 px-2 rounded-md bg-muted/30 hover:bg-primary/10 border border-border/60 hover:border-primary/40 transition-colors group"
                      title={`Open ${r.name} in editor`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-foreground truncate flex-1 group-hover:text-primary">
                          {renderHighlighted(r.name, query)}
                        </span>
                        {modified && (
                          <span className="text-[10px] text-muted-foreground font-medium shrink-0 whitespace-nowrap">{modified}</span>
                        )}
                      </div>
                      {r.snippet && (
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                          {renderHighlighted(r.snippet, query)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </PanelSection>
  );
}
